import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Menu, Typography, Spin, message } from 'antd';
import {
  EditOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  TableOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ArrowLeftOutlined,
  ImportOutlined,
  UnorderedListOutlined,
  VideoCameraOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { TournamentService } from '@/services/tournament.service';
import { Tournament } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';
import { TournamentEditForm } from './TournamentEditForm';
import RegistrationConfirmation from './RegistrationConfirmation';
import WeighInPage from './WeighInPage';
import { TatamiDistribution } from './TatamiDistribution';
import { ParticipantImport } from './ParticipantImport';
import { DrawsPage } from './DrawsPage';
import { LiveStreamSetup } from './LiveStreamSetup';
import { TournamentCategoriesEditor } from './TournamentCategoriesEditor';

const { Sider, Content } = Layout;
const { Title } = Typography;

type MenuItem = Required<MenuProps>['items'][number];

export const TournamentManagementPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState<string>('info');
  const [hasMatches, setHasMatches] = useState(false);
  const [bracketsPublished, setBracketsPublished] = useState(false);
  const [publishingBrackets, setPublishingBrackets] = useState(false);

  // Проверяем, является ли пользователь организатором турнира
  const isOrganizer = tournament && user && (
    user.role === 'admin' ||
    tournament.organizerId === user.id ||
    (user.role === 'manager' && tournament.clubId === user.clubId) ||
    (user.role === 'manager' && tournament.clubId === user.clubId)
  );

  useEffect(() => {
    const fetchTournament = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const tournamentData = await TournamentService.getById(Number(id));
        setTournament(tournamentData);
        
        // Проверяем наличие матчей
        try {
          const matches = await TournamentService.getTournamentMatches(Number(id));
          const matchesArray = Array.isArray(matches) ? matches : (matches as any)?.data || [];
          setHasMatches(matchesArray.length > 0);
        } catch (error) {
          logger.error('Error checking matches:', error);
          setHasMatches(false);
        }
        
        // Проверяем статус публикации сеток
        if (tournamentData) {
          setBracketsPublished(tournamentData.bracketsPublished || false);
        }
      } catch (error) {
        logger.error('Error fetching tournament:', error);
        message.error('Ошибка при загрузке турнира');
      } finally {
        setLoading(false);
      }
    };

    fetchTournament();
  }, [id]);

  const handleMenuClick = ({ key }: { key: string }) => {
    setActiveKey(key);
  };

  const handleEditSuccess = async () => {
    if (id) {
      try {
        const tournamentData = await TournamentService.getById(Number(id));
        setTournament(tournamentData);
        message.success('Турнир успешно обновлен');
        // Обновляем информацию о матчах
        try {
          const matches = await TournamentService.getTournamentMatches(Number(id));
          const matchesArray = Array.isArray(matches) ? matches : (matches as any)?.data || [];
          setHasMatches(matchesArray.length > 0);
        } catch (error) {
          logger.error('Error checking matches:', error);
          setHasMatches(false);
        }
        // Обновляем статус публикации сеток
        if (tournamentData) {
          setBracketsPublished(tournamentData.bracketsPublished || false);
        }
      } catch (error) {
        logger.error('Error fetching tournament after update:', error);
      }
    }
  };

  const handlePublishBrackets = async () => {
    if (!id) return;
    
    try {
      setPublishingBrackets(true);
      await TournamentService.publishBrackets(Number(id));
      message.success('Сетки успешно опубликованы. PDF генерируется в фоновом режиме.');
      
      // Обновляем данные турнира
      const tournamentData = await TournamentService.getById(Number(id));
      setTournament(tournamentData);
      if (tournamentData) {
        setBracketsPublished(tournamentData.bracketsPublished || false);
      }
    } catch (error: any) {
      logger.error('Error publishing brackets:', error);
      message.error(error?.response?.data?.message || 'Ошибка при публикации сеток');
    } finally {
      setPublishingBrackets(false);
    }
  };


  const menuItems: MenuItem[] = [
    {
      key: 'info',
      icon: <EditOutlined />,
      label: 'Информация о турнире',
    },
    {
      key: 'categories',
      icon: <AppstoreOutlined />,
      label: 'Категории',
    },
    {
      key: 'import',
      icon: <ImportOutlined />,
      label: 'Импорт участников',
    },
    {
      key: 'registration',
      icon: <CheckCircleOutlined />,
      label: 'Подтверждение регистрации',
    },
    {
      key: 'weigh-in',
      icon: <DashboardOutlined />,
      label: 'Взвешивание',
    },
    {
      key: 'draws',
      icon: <UnorderedListOutlined />,
      label: 'Жеребьевка',
    },
    ...(hasMatches ? [{
      key: 'tatami-distribution',
      icon: <TableOutlined />,
      label: 'Распределение по татами',
    }] : []),
    {
      key: 'broadcast',
      icon: <VideoCameraOutlined />,
      label: 'Трансляции',
    },
  ];

  if (loading || !tournament) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Проверка прав доступа
  if (!isOrganizer) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Title level={3}>У вас нет доступа к управлению этим турниром</Title>
        <button onClick={() => navigate(`/tournaments/${id}`)}>
          Вернуться к турниру
        </button>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeKey) {
      case 'info':
        return <TournamentEditForm tournament={tournament} onSuccess={handleEditSuccess} />;
      case 'categories':
        return (
          <TournamentCategoriesEditor tournament={tournament} onSuccess={handleEditSuccess} />
        );
      case 'import':
        return <ParticipantImport tournamentId={Number(id)} />;
      case 'registration':
        return <RegistrationConfirmation tournamentId={Number(id)} />;
      case 'weigh-in':
        return <WeighInPage tournamentId={Number(id)} />;
      case 'draws':
        return (
          <DrawsPage 
            tournamentId={Number(id)}
            onPublishBrackets={handlePublishBrackets}
            onBracketsGenerated={handleEditSuccess}
            bracketsPublished={bracketsPublished}
            hasMatches={hasMatches}
            publishingBrackets={publishingBrackets}
            tournamentDate={tournament?.tournamentDate}
          />
        );
      case 'tatami-distribution':
        return hasMatches ? <TatamiDistribution tournamentId={Number(id)} /> : null;
      case 'broadcast':
        return <LiveStreamSetup tournament={tournament} onUpdate={handleEditSuccess} />;
      default:
        return <TournamentEditForm tournament={tournament} onSuccess={handleEditSuccess} />;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 64,
          bottom: 0,
          background: '#001529',
        }}
        width={250}
      >
        <div style={{ 
          padding: '16px', 
          color: 'white', 
          textAlign: 'center',
          borderBottom: '1px solid #1890ff',
          marginBottom: 8
        }}>
          <Title level={4} style={{ color: 'white', margin: 0 }}>
            {collapsed ? 'Упр.' : 'Управление турниром'}
          </Title>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[activeKey]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 250, transition: 'margin-left 0.2s' }}>
        <div style={{ 
          position: 'fixed',
          left: collapsed ? 80 : 250,
          top: 64,
          zIndex: 100,
          background: '#fff',
          padding: '8px 16px',
          borderRadius: '0 0 8px 0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'left 0.2s',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
        >
          <div onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
          <div 
            onClick={() => navigate(`/tournaments/${id}`)}
            style={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#1890ff'
            }}
          >
            <ArrowLeftOutlined />
            <span>Вернуться к турниру</span>
          </div>
        </div>
        <Content
          style={{
            margin: '24px',
            padding: '24px',
            background: '#fff',
            borderRadius: '8px',
            minHeight: 'calc(100vh - 112px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            marginTop: '64px',
          }}
        >
          <div style={{ marginBottom: '24px' }}>
            <Title level={2} style={{ margin: 0 }}>
              {tournament.name}
            </Title>
            <div style={{ color: '#8c8c8c', marginTop: '8px' }}>
              Управление турниром
            </div>
          </div>
          
          {renderContent()}
        </Content>
      </Layout>
    </Layout>
  );
};

export default TournamentManagementPage;
